import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LineWave } from "react-loader-spinner";
import { FaSortUp, FaSortDown } from "react-icons/fa";
import { MdDelete } from "react-icons/md";

import { toast } from "react-toastify";
import ReactPaginate from "react-paginate";
import { getSidekickProducts } from "../../../redux/slices/sidekickProductsSlice";
import { updateBookApproval } from "../../../redux/slices/previewBooksSlice";
import Confirmation from "../../../components/confirmation/Confirmation";

export const Tooltip = ({ text, children }) => (
  <div className="relative group">
    {children}
    <div
      className={`absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-max p-1 bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
    >
      {text}
    </div>
  </div>
);

export default function SideKickProducts() {
  const dispatch = useDispatch();
  const { products, isLoading } = useSelector(
    (state) => state.sidekickproductReducer
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [sortBy, setSortBy] = useState("publishedOn");
  const [sortOrder, setSortOrder] = useState("desc");
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [prductForDeletion, setPrductForDeletion] = useState("");
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const handlePagination = (e) => {
    setCurrentPage(e.selected + 1);
  };

  const fetchproducts = async function () {
    const response = await dispatch(
      getSidekickProducts({
        currentPage,
        debouncedSearchTerm,
        sortBy,
        sortOrder,
      })
    );
    if (response?.payload?.code !== 200) {
      toast.error("Record not found");
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const newTimeout = setTimeout(() => {
      setDebouncedSearchTerm(value);
    }, 2000);

    setDebounceTimeout(newTimeout);
  };

  const handleSort = (column, order) => {
    setSortBy(column);
    setSortOrder(order);
  };

  const handleReset = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSortBy("publishedOn");
    setSortOrder("desc");
    setCurrentPage(1);
  };

  const TableHeader = ({ title, sortColumn }) => {
    return (
      <th scope="col" className="px-2 py-3 text-center w-[150px]">
        <div className="flex items-center justify-center">
          <span>{title}</span>
          {sortColumn && (
            <div className="ml-2 flex flex-col items-center">
              <FaSortUp
                size={14}
                className={`text-blue-500 cursor-pointer relative ${sortBy === sortColumn && sortOrder === "asc"
                  ? "opacity-100"
                  : "opacity-50"
                  }`}
                onClick={() => handleSort(sortColumn, "asc")}
              />
              <FaSortDown
                size={14}
                className={`text-blue-500 cursor-pointer relative4 ${sortBy === sortColumn && sortOrder === "desc"
                  ? "opacity-100"
                  : "opacity-50"
                  }`}
                onClick={() => handleSort(sortColumn, "desc")}
              />
            </div>
          )}
        </div>
      </th>
    );
  };

  const onClickDeleteProductHandler = (product) => {
    setOpenConfirmationModal(true);
    setPrductForDeletion(product.slug);
  };

  const onDeleteProductHandler = async () => {
    setIsDeleteLoading(true);
    const payload = {
      isDeleted: true,
      slug: prductForDeletion,
    };
    try {
      const response = await dispatch(updateBookApproval(payload));

      if (response?.payload?.code === 200) {
        await fetchproducts();
        toast.success("Product has been deleted successfully");
      } else {
        toast.error(
          response?.payload?.response?.data?.message,
          "Something went wrong"
        );
      }
    } catch (_error) {
      toast.error("Something went wrong");
    }
    setOpenConfirmationModal(false);
    setPrductForDeletion("");
    setIsDeleteLoading(false);
  };

  const cancelModalHandler = () => {
    setOpenConfirmationModal(false);
    setPrductForDeletion("");
  };

  useEffect(() => {
    fetchproducts();
  }, [currentPage, debouncedSearchTerm, sortBy, sortOrder]);

  return (
    <>
      <div>
        <h1 className="text-left text-white text-3xl font-bold mb-4">
          Published Products
        </h1>
        <div className="mb-4 flex justify-stretch gap-2">
          <div className="relative w-[560px] rounded-full bg-[#372F5B]">
            <input
              type="text"
              placeholder="Search by Product Name"
              onChange={handleSearchChange}
              value={searchTerm}
              className="bg-[#372F5B] h-10 px-4 py-2 pl-[35px] text-white rounded-full text-sm focus:outline-none w-full max-w-[400px]"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setDebouncedSearchTerm("");
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-500 w-6 h-6 flex items-center justify-center"
              >
                ✖
              </button>
            )}
          </div>

          <button
            onClick={handleReset}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
          >
            Reset
          </button>
        </div>
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left rtl:text-right text-white dark:text-gray-400 table-fixed">
            <thead className="text-xs text-gray-700 uppercase bg-gray-300 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <TableHeader title="Product Name" sortColumn="title" />
                <TableHeader title="Category" sortColumn="category" />
                <TableHeader title="Price Type" sortColumn="priceType" />
                <TableHeader
                  title="Included in Subscription"
                  sortColumn="subscription"
                />
                <TableHeader
                  title="# Redeemed"
                  sortColumn="subscriptionCount"
                />
                <TableHeader title="# Purchased" sortColumn="paidCount" />
                <TableHeader
                  title="Total Claimed"
                  sortColumn="totalClaimedTypesCount"
                />
                <TableHeader
                  title="Average Session Time (In Minutes)"
                  sortColumn="averageTime"
                />
                <TableHeader title="Action" sortColumn="" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="loader !bg-transparent ml-[18%]">
                    <LineWave color={"#fff"} loading={isLoading} size={50} />
                  </td>
                </tr>
              ) : products?.data?.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-4 text-center text-2xl text-gray-500"
                  >
                    No products Found
                  </td>
                </tr>
              ) : (
                products?.data?.map((product) => (
                  <tr key={product?._id} className="bg-[#211849] border-b">
                    <td className="px-6 py-4 text-center">
                      {product?.title || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-center capitalize">
                      {product?.category || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-center capitalize">
                      {product?.priceType || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {product?.subscription ? "Yes" : "No"}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {product?.subscriptionCount || 0}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {product?.paidCount || 0}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {product?.totalClaimedTypesCount || 0}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {product?.averageTime || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Tooltip text="Delete">
                        <button
                          className="bg-red-600 text-white py-1 px-2 text-[12px] rounded-lg hover:bg-red-700 transition-colors duration-300 cursor-pointer"
                          onClick={() => {
                            onClickDeleteProductHandler(product);
                          }}
                        >
                          <MdDelete />
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && products?.data?.length !== 0 && (
          <div className="flex justify-end py-8">
            <ReactPaginate
              previousLabel="Previous"
              nextLabel="Next"
              onPageChange={handlePagination}
              breakLabel="..."
              breakClassName="mx-2 text-white"
              pageCount={products?.meta?.total_pages}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              containerClassName="flex justify-center"
              previousClassName="px-4 py-2 bg-red-600 text-white rounded-l cursor-pointer"
              nextClassName="px-4 py-2 bg-red-600 text-white rounded-r cursor-pointer"
              activeClassName="px-4 py-2 bg-red-500 text-white"
              pageClassName="px-4 py-2 bg-[#372F5B] text-white"
              forcePage={currentPage - 1}
            />
          </div>
        )}
      </div>
      {openConfirmationModal && (
        <Confirmation
          confirmDelete={onDeleteProductHandler}
          cancelDelete={cancelModalHandler}
          buttonText="Delete"
          message="delete this Product"
        />
      )}
      {isDeleteLoading && (
        <div className="loader">
          <LineWave color={"#fff"} loading={isDeleteLoading} size={150} />
        </div>
      )}
    </>
  );
}
